// Constraints between objects in the UI: each kind's pieces, the solve and its
// refusals, the Constrain tool, the constraints following their objects after an
// edit, the one list of them all, the badges, the selected constraint and its
// panel, the Constraints tab. docs/claude/constraints.md.
import {
  type ConstraintFace,
  type ConstraintRef,
  type ObjConstraintType,
  type ObjectConstraint,
  type ProductInstance,
  type SaunaDoor,
  type ShapeRelation,
  type Vec2,
} from '../../shared/types';
import {
  itemsOf,
  MULTI_KINDS,
  selectConstraint,
  selectedCount,
  selectionOfIds,
  selectOne,
} from '../selection';
import { type ToolOption } from '../commands';
import { connectionEndpointName } from './connectionUi';
import {
  flashStatus,
  flashToolStatus,
  invalidate,
  newId,
  objHidden,
  objLocked,
  pushHistory,
  renderProperties,
  type SnapKind,
  state,
  updateStatus,
  vp,
} from './core';
import { showPrompt } from '../dialog';
import { openFigureEditor } from './dimEditor';
import {
  drawingDimStyle,
  formatDimension,
} from '../dimFormat';
import { type GcsSketch } from '../gcs';
import {
  distance,
  distToSegment,
  ellipseFromRing,
  ellipseQuadrants,
  instanceToWorld,
  polyBenchLength,
  toInstanceLocal,
} from '../geometry';
import {
  groupables,
  groupMembers,
  outermostGroup,
} from '../groups';
import { tr } from '../i18n';
import { type ItemKind } from '../layers';
import {
  applyMove,
  type BadgeBox,
  type BadgeSpec,
  type BodyInput,
  BROKEN_TOL,
  CONSTRAINT_META,
  constraintError,
  frameOf,
  isNoMove,
  layoutBadges,
  matchPiece,
  measure,
  type NetworkInput,
  networkOf,
  networkStatus,
  orderRefs,
  ownAxesSketch,
  refsOf,
  relationsFor,
  solveNetwork,
} from '../objConstraints';
import {
  hoverPartId,
  partObjectRow,
  selectObjectFromList,
  showObjectRowMenu,
} from './partsTab';
import {
  isPolyRoom,
  polyEdgeLengths,
  polyEdges,
  rescaleEdgeFeatures,
  unlockWallSpan,
  wallChain,
  wallRuns,
  wallView,
} from '../polyRoom';
import { instanceCorners } from '../render';
import { sectionLabel } from '../section';
import { textBoxCorners } from '../textBox';
import {
  wallBand,
  wallSegPins,
} from '../wallTool';
import {
  arcEndPoints,
  deselectAll,
  dropLayerCache,
  regenerateSauna,
  roomContents,
  screenPos,
  selectedObjectIds,
  syncDimensions,
  transformObjectsIn,
  undoSteps,
  zoomToPoints,
} from '../main';
import { recenterPolyRoom } from './vertexEdit';
import { openContextMenu } from './contextMenus';
import {
  buttonRow,
  deleteRow,
  evalFormula,
  infoRow,
  numberRow,
  parseMeasure,
  sectionTitle,
} from './panelRows';
import {
  addPolyRelation,
  addRoomRelation,
  buildRingSketch,
  polyRingCons,
  roomRingCons,
} from './ringConstraints';
import { followVertices } from '../anchors';

/** This module's state that main.ts reads and writes too. */
export const constraintUi = {
  /** The drawing revision the last reconcile ran at (`frame` skips it until the
   *  drawing changes or a drag is live). */
  constraintRevision: -1,
  constrainType: null as ObjConstraintType | null,
  /** Add relation… on a selection: picks are limited to these objects. */
  constrainScope: null as Set<string> | null,
  /** The constraint picked by its badge or its Parts-tab row. Only meaningful while
   *  no object is selected (`selectedConstraint`). */
  /** The constraints a refusal named: shown and lit until the next press. */
  constraintClashIds: new Set<string>(),
  /** The constraint whose badge is under the cursor. */
  hoverConstraintId: null as string | null,
  /** Tab / Shift+Tab with the Constrain tool: which of the pieces stacked under the
   *  pointer is offered, and where the pointer was (moving away resets it). */
  constrainCycle: null as { sp: Vec2; idx: number } | null,
  constrainHover: null as CPiece | null,
};

/** A pickable piece of an object, in world mm. `ctrl` ties its points to the
 *  object's control vertices when the object can deform. */
interface CPiece {
  ref: ConstraintRef; pts: Vec2[]; ctrl?: Array<{ i: number; off: Vec2 }>;
  /** What is drawn and picked for a curve (its arc / ring); `pts` is its centre + a rim point. */
  outline?: Vec2[];
}
interface CGeom {
  pieces: CPiece[];
  /** Present when the object may reshape to satisfy a constraint (stage 2). */
  deform?: { ctrl: Vec2[]; closed: boolean; own?: GcsSketch; pin?: number[]; set: (verts: Vec2[]) => void };
}

const offOf = (p: Vec2, c: Vec2): Vec2 => ({ x: p.x - c.x, y: p.y - c.y });

/** Vertex and edge pieces of a run of points (an outline, a polyline, a face). */
function runPieces(obj: string, pts: Vec2[], closed: boolean, face?: ConstraintFace, ctrlIdx?: (k: number) => number, ctrlPts?: Vec2[], vertexBase = 0, edgeBase = 0): CPiece[] {
  const out: CPiece[] = [];
  const n = pts.length;
  const ct = (k: number): { i: number; off: Vec2 } | undefined => {
    if (!ctrlIdx || !ctrlPts) return undefined;
    const i = ctrlIdx(k);
    return { i, off: offOf(pts[k], ctrlPts[i]) };
  };
  pts.forEach((p, k) => {
    const c = ct(k);
    out.push({ ref: { obj, el: 'vertex', index: vertexBase + k, ...(face ? { face } : {}) }, pts: [p], ...(c ? { ctrl: [c] } : {}) });
  });
  const segs = closed ? n : n - 1;
  for (let k = 0; k < segs; k++) {
    const j = (k + 1) % n;
    const a = ct(k), b = ct(j);
    out.push({ ref: { obj, el: 'edge', index: edgeBase + k, ...(face ? { face } : {}) }, pts: [pts[k], pts[j]], ...(a && b ? { ctrl: [a, b] } : {}) });
  }
  return out;
}

/** Every piece a constraint can attach to, by the object's kind (`constraintGeom`):
 *  undefined when the id is not of that kind, null when the object is there but
 *  not constrainable. Every ItemKind has an entry, so a new kind does not
 *  typecheck until it says what it offers a constraint (null: nothing). */
type ConstraintGeomOf = (id: string, withDeform: boolean) => CGeom | null | undefined;
const CONSTRAINT_GEOM: Record<ItemKind, ConstraintGeomOf | null> = {
  instance: (id, withDeform) => {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (inst) {
      if (inst.nodes && inst.nodes.length >= 2) {
        const ctrl = inst.nodes.map((p) => ({ ...p }));
        return {
          pieces: runPieces(id, ctrl, false, undefined, (k) => k, ctrl),
          deform: { ctrl, closed: false, set: (v) => {
            inst.nodes = v.map((p) => ({ ...p }));
            inst.x = v.reduce((s, n) => s + n.x, 0) / v.length;
            inst.y = v.reduce((s, n) => s + n.y, 0) / v.length;
            inst.width = Math.round(polyBenchLength(inst.nodes));
          } },
        };
      }
      if (isPolyRoom(inst)) {
        // By WALL corner, never by pane jamb: a pane added to a wall must not
        // renumber the pieces a constraint points at.
        const view = wallView(inst);
        const edges = polyEdges(view);
        const runs = wallRuns(inst);
        if (!edges.length || runs.length !== edges.length) return null;
        const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
        const ctrl = inst.polygon!.map(W);
        const outer = edges.map((e) => W(e.a)), inner = edges.map((e) => W(e.ia));
        const cIdx = (k: number): number => runs[k].a;
        const pieces = [...runPieces(id, outer, true, 'outer', cIdx, ctrl), ...runPieces(id, inner, true, 'inner', cIdx, ctrl)];
        // A room reshapes like a polygon. Its locks are in its OWN axes, so for a
        // turned room they are re-expressed against them (`roomAxesSketch`); what
        // stands in it stays where it is, as in vertex edit.
        return {
          pieces,
          deform: withDeform ? {
            ctrl, closed: true, own: roomAxesSketch(inst, buildRingSketch(ctrl.map((p) => ({ x: p.x, y: p.y })), roomRingCons(inst)).sketch),
            set: (v) => {
              const before = polyEdgeLengths(inst);
              inst.polygon = v.map((p) => toInstanceLocal(inst, p));
              recenterPolyRoom(inst);
              followVertices(state.drawing, inst.id, () => rescaleEdgeFeatures(inst, before));
              regenerateSauna(inst);
            },
          } : undefined,
        };
      }
      // A part: its footprint corners, its centre, its sides. Never deforms.
      const cs = instanceCorners(inst);
      const pieces = runPieces(id, cs, true);
      pieces.push({ ref: { obj: id, el: 'vertex', index: 4 }, pts: [{ x: inst.x, y: inst.y }] });
      return { pieces };
    }
    return undefined;
  },
  polygon: (id, withDeform) => {
    const poly = (state.drawing.polygons || []).find((p) => p.id === id);
    if (poly) {
      if (poly.ellipse) {
        const fit = ellipseFromRing(poly.points);
        if (!fit) return null;
        const quads = ellipseQuadrants(fit);
        const pts = [fit.center, ...quads];
        const pieces: CPiece[] = pts.map((p, k) => ({ ref: { obj: id, el: 'vertex' as const, index: k }, pts: [p] }));
        // A CIRCLE is also a curve: its centre and a rim point, scaled as one.
        const circle = Math.abs(fit.rx - fit.ry) <= Math.max(fit.rx, fit.ry) * 0.01;
        if (!circle) return { pieces };
        const ctrl = [{ ...fit.center }, { ...quads[0] }];
        const zero = [{ i: 0, off: { x: 0, y: 0 } }, { i: 1, off: { x: 0, y: 0 } }];
        pieces.push({ ref: { obj: id, el: 'curve', index: 0 }, pts: ctrl, ctrl: zero, outline: [...poly.points, poly.points[0]] });
        return {
          pieces,
          deform: { ctrl, closed: false, pin: [0], set: (v) => {
            const r0 = Math.hypot(ctrl[1].x - ctrl[0].x, ctrl[1].y - ctrl[0].y) || 1;
            const k = Math.hypot(v[1].x - v[0].x, v[1].y - v[0].y) / r0;
            poly.points = poly.points.map((p) => ({ x: v[0].x + (p.x - ctrl[0].x) * k, y: v[0].y + (p.y - ctrl[0].y) * k }));
          } },
        };
      }
      const n = poly.points.length;
      const ctrl = poly.points.map((p) => ({ ...p }));
      const pieces = runPieces(id, ctrl, true, undefined, (k) => k, ctrl);
      // Holes: their vertices and edges number on after the outer ring's.
      let vb = n, eb = n;
      for (const h of poly.holes || []) { pieces.push(...runPieces(id, h, true, undefined, undefined, undefined, vb, eb)); vb += h.length; eb += h.length; }
      return {
        pieces,
        deform: poly.holes?.length || !withDeform ? undefined : {
          ctrl, closed: true, own: buildRingSketch(ctrl.map((p) => ({ x: p.x, y: p.y })), polyRingCons(poly)).sketch,
          set: (v) => { poly.points = v.map((p) => ({ ...p })); },
        },
      };
    }
    return undefined;
  },
  wall: (id, withDeform) => {
    const wall = (state.drawing.walls || []).find((w) => w.id === id);
    if (wall) {
      const ctrl = wall.points.map((p) => ({ ...p }));
      const set = (v: Vec2[]): void => { wall.points = v.map((p) => ({ ...p })); };
      if (wall.style === 'line') return { pieces: runPieces(id, ctrl, false, undefined, (k) => k, ctrl), deform: { ctrl, closed: false, set } };
      // A wall's faces, not its centre line: what is drawn and dimensioned.
      const n = ctrl.length;
      const band = wallBand(ctrl, wall.thickness, wall.align);
      if (band.length !== 2 * n) return null;
      const left = band.slice(0, n), right = band.slice(n).reverse();
      return {
        pieces: [...runPieces(id, left, false, 'left', (k) => k, ctrl), ...runPieces(id, right, false, 'right', (k) => k, ctrl)],
        deform: { ctrl, closed: false, set },
      };
    }
    return undefined;
  },
  text: (id, withDeform) => {
    const text = (state.drawing.texts || []).find((t) => t.id === id);
    if (text) return { pieces: runPieces(id, textBoxCorners(text), true) };
    return undefined;
  },
  arc: (id, withDeform) => {
    const arc = (state.drawing.arcs || []).find((a) => a.id === id);
    if (arc) {
      const ends = arcEndPoints(arc);
      const pts = [...ends, { ...arc.center }];
      const pieces: CPiece[] = pts.map((p, k) => ({ ref: { obj: id, el: 'vertex' as const, index: k }, pts: [p] }));
      // The arc as a curve: its centre and its start; the rest of it is its sweep.
      const ctrl = [{ ...arc.center }, { ...ends[0] }];
      const sweep = ((arc.endDeg - arc.startDeg) % 360 + 360) % 360 || 360;
      const outline = Array.from({ length: 33 }, (_, k) => {
        const t = (arc.startDeg + sweep * k / 32) * Math.PI / 180;
        return { x: arc.center.x + arc.radius * Math.cos(t), y: arc.center.y + arc.radius * Math.sin(t) };
      });
      pieces.push({ ref: { obj: id, el: 'curve', index: 0 }, pts: ctrl, ctrl: [{ i: 0, off: { x: 0, y: 0 } }, { i: 1, off: { x: 0, y: 0 } }], outline });
      return {
        pieces,
        deform: { ctrl, closed: false, pin: [0], set: (v) => {
          arc.center = { ...v[0] };
          arc.radius = Math.hypot(v[1].x - v[0].x, v[1].y - v[0].y);
        } },
      };
    }
    return undefined;
  },
  leader: (id, withDeform) => {
    const leader = (state.drawing.leaders || []).find((l) => l.id === id);
    if (leader) {
      const ctrl = leader.points.map((p) => ({ ...p }));
      return { pieces: runPieces(id, ctrl, false, undefined, (k) => k, ctrl), deform: { ctrl, closed: false, set: (v) => { leader.points = v.map((p) => ({ ...p })); } } };
    }
    return undefined;
  },
  cloud: (id, withDeform) => {
    const cloud = (state.drawing.revisionClouds || []).find((c) => c.id === id);
    if (cloud) {
      const ctrl = cloud.points.map((p) => ({ ...p }));
      return { pieces: runPieces(id, ctrl, true, undefined, (k) => k, ctrl), deform: { ctrl, closed: true, set: (v) => { cloud.points = v.map((p) => ({ ...p })); } } };
    }
    return undefined;
  },
  // A section line only takes a Fix (the Constrain tool picks from `groupables`):
  // its ends are what an edit is checked against, and put back.
  section: (id) => {
    const sec = (state.drawing.sections || []).find((x) => x.id === id);
    return sec ? { pieces: runPieces(id, [{ ...sec.a }, { ...sec.b }], false) } : undefined;
  },
  // Not constrainable: a floor, a connection (it follows its ends), a schedule
  // table, a dimension (a distance constraint is one already).
  flooring: null,
  conn: null,
  rcp: null,
  table: null,
  dim: null,
};

/** Every piece a constraint can attach to on object `id`, or null when the
 *  object is not constrainable (or no longer exists). `withDeform` false skips
 *  building the reshape model (its own sketch) — the per-frame readers' case. */
export function constraintGeom(id: string, withDeform = true): CGeom | null {
  for (const kind of Object.keys(CONSTRAINT_GEOM) as ItemKind[]) {
    const g = CONSTRAINT_GEOM[kind]?.(id, withDeform);
    if (g !== undefined) return g;
  }
  return null;
}

const sameRef = (a: ConstraintRef, b: ConstraintRef): boolean => a.obj === b.obj && a.el === b.el && a.index === b.index && (a.face ?? '') === (b.face ?? '');

/**
 * A room's own sketch (built over its world outline) with the locks that are in
 * the room's OWN axes re-expressed for a turned room: a horizontal / vertical
 * edge becomes an angle of 0° / 90° to the room's x axis (a fixed reference
 * edge), and a Lock X / Lock Y a point on the room's own vertical / horizontal
 * line. An unturned room's sketch is returned as it is.
 */
function roomAxesSketch(inst: ProductInstance, sk: GcsSketch): GcsSketch {
  if ((((inst.rotation % 360) + 360) % 360) === 0) return sk;
  return ownAxesSketch(sk, (p) => instanceToWorld(inst, p));
}

/** What a reference points at now, or null when it no longer exists. */
function constraintPiece(ref: ConstraintRef): CPiece | null {
  return constraintGeom(ref.obj, false)?.pieces.find((p) => sameRef(p.ref, ref)) ?? null;
}
/** What a reference looks like on the drawing: a curve's ring / arc, else its points. */
function constraintPieceOutline(ref: ConstraintRef): Vec2[] {
  const p = constraintPiece(ref);
  return p ? p.outline ?? p.pts : [];
}

export function objConstraints(): ObjectConstraint[] { return state.drawing.constraints ?? []; }
/** Every object a constraint may name: the groupable ones and the section lines
 *  (which only take a Fix). */
function constrainables(): Array<{ id: string }> { return [...groupables(state.drawing), ...(state.drawing.sections || [])]; }
/** Object ids pinned by a Fix constraint. */
function fixedObjectIds(): Set<string> { return new Set(objConstraints().filter((c) => c.type === 'fix').map((c) => c.a.obj)); }
export function isFixedObject(id: string): boolean { return objConstraints().some((c) => c.type === 'fix' && c.a.obj === id); }
/** Every object a constraint refers to. */
function constrainedObjectIds(cs = objConstraints()): Set<string> {
  const s = new Set<string>();
  for (const c of cs) for (const r of refsOf(c)) s.add(r.obj);
  return s;
}

/** The rigid body an object moves as: a whole (outermost) group, else the object
 *  itself — a room taking along what stands in it, unless that is constrained in
 *  its own right (then it is a body of its own). `key` names the body. */
function constraintBody(id: string, constrained = constrainedObjectIds()): { key: string; members: string[] } {
  const all = groupables(state.drawing);
  const o = all.find((x) => x.id === id);
  const outer = o ? outermostGroup(state.drawing, o) : undefined;
  const base = outer ? groupMembers(state.drawing, outer).map((m) => m.id) : [id];
  const members = new Set(base);
  for (const mid of base) {
    const room = state.drawing.instances.find((i) => i.id === mid && i.autoBenches);
    if (!room) continue;
    for (const c of roomContents(room)) if (!constrained.has(c.id) && !c.groupId && !objLocked(c.id)) members.add(c.id);
  }
  return { key: outer ? `g:${outer}` : `o:${id}`, members: [...members] };
}

/** Move / turn a set of objects through `fn` (`rotDeg` turns parts and text) —
 *  through `transformObjectsIn`, the same code as Move / Rotate, so a room still
 *  re-lays its benches and a text box still turns. */
function transformObjectIds(ids: Iterable<string>, fn: (p: Vec2) => Vec2, rotDeg: number): void {
  transformObjectsIn(itemsOf(state.drawing, selectionOfIds(state.drawing, ids)), fn, rotDeg, 1);
}

/**
 * Solve the constraints connected to `seeds` (object ids) and apply the result.
 * `held` objects (and every fixed one) do not move; the rest move as rigid bodies,
 * a lone deformable shape reshaping only when that fails. `cs` overrides the
 * constraint list (a tentative new constraint). Returns false — and changes
 * nothing — when the network cannot be satisfied.
 */
function solveConstraints(seeds: Iterable<string>, held: Iterable<string>, opts: { cs?: ObjectConstraint[]; live?: boolean; dry?: boolean; pull?: string[] } = {}): boolean {
  const net = constraintNetworkInput(seeds, held, opts.cs);
  if (!net) return true;
  const res = solveNetwork(net.input, { live: opts.live, pull: opts.pull?.map(net.bodyOf) });
  if (!res.ok) return false;
  if (opts.dry) return true;
  for (const [key, m0] of res.moves) {
    // A turn of solver noise is no turn: it would leave 1e-5° on a part's rotation.
    const m = Math.abs(m0.rot) < 1e-6 ? { ...m0, rot: 0 } : m0;
    if (isNoMove(m)) continue;
    transformObjectIds(net.members.get(key) ?? [], (p) => applyMove(m, p), (m.rot * 180) / Math.PI);
  }
  for (const [obj, verts] of res.deformed) constraintGeom(obj)?.deform?.set(verts);
  return true;
}

/**
 * The network of constraints reaching `seeds` (object ids) as the pure solver's
 * input: its bodies, each body's frame, which are held (`held`, and every object
 * a Fix in `cs` pins) and which may deform. Null when the seeds take part in no
 * relation. `cs` overrides the drawing's list (a tentative constraint, a trial
 * without one).
 */
function constraintNetworkInput(seeds: Iterable<string>, held: Iterable<string>, csIn?: ObjectConstraint[]): { input: NetworkInput; members: Map<string, string[]>; bodyOf: (id: string) => string } | null {
  const cs = csIn ?? objConstraints();
  const constrained = constrainedObjectIds(cs);
  const bodyCache = new Map<string, { key: string; members: string[] }>();
  const bodyOfObj = (id: string): { key: string; members: string[] } => {
    let b = bodyCache.get(id);
    if (!b) { b = constraintBody(id, constrained); bodyCache.set(id, b); }
    return b;
  };
  const bodyOf = (id: string): string => bodyOfObj(id).key;
  const seedBodies = [...seeds].map(bodyOf);
  const net = networkOf(cs, bodyOf, seedBodies);
  if (!net.some((c) => c.type !== 'fix')) return null;
  const heldBodies = new Set([...held].map(bodyOf));
  for (const c of cs) if (c.type === 'fix') heldBodies.add(bodyOf(c.a.obj));
  // A locked layer's objects are never edited: its constrained partners follow
  // them, never the other way round.
  for (const c of net) for (const r of refsOf(c)) if (objLocked(r.obj)) heldBodies.add(bodyOf(r.obj));
  const bodies = new Map<string, BodyInput>();
  const members = new Map<string, string[]>();
  for (const c of net) for (const r of refsOf(c)) {
    const b = bodyOfObj(r.obj);
    if (bodies.has(b.key)) continue;
    // The frame: two far-apart points of the body's constrainable members.
    const pts: Vec2[] = [];
    for (const m of b.members) for (const p of constraintGeom(m, false)?.pieces ?? []) if (p.ref.el === 'vertex') pts.push(p.pts[0]);
    const frame = frameOf(pts) ?? [pts[0] ?? { x: 0, y: 0 }, { x: (pts[0]?.x ?? 0) + 1000, y: pts[0]?.y ?? 0 }];
    // One object, or a room with what stands in it (it reshapes; they stay put).
    const g = b.members.length === 1 || b.key === `o:${b.members[0]}` ? constraintGeom(b.members[0]) : null;
    bodies.set(b.key, {
      frame, held: heldBodies.has(b.key),
      deform: g?.deform ? { obj: b.members[0], verts: g.deform.ctrl, closed: g.deform.closed, own: g.deform.own, pin: g.deform.pin } : undefined,
    });
    members.set(b.key, b.members);
  }
  const input: NetworkInput = {
    constraints: net, bodyOf, bodies,
    piece: (ref) => { const p = constraintPiece(ref); return p ? { pts: p.pts, ctrl: p.ctrl } : null; },
  };
  return { input, members, bodyOf };
}

/** How defined object `id` is by its constraints: `'free'` (it can still move),
 *  `'defined'` (pinned — to a fixed object, or with none, to its partners), or
 *  `'broken'` (the drawing no longer satisfies its network). Null: unconstrained. */
function constraintStatusOf(id: string): 'free' | 'defined' | 'broken' | null {
  if (isFixedObject(id)) return 'defined';
  const net = constraintNetworkInput([id], []);
  if (!net) return null;
  const st = networkStatus(net.input);
  if (!st) return null;
  if (st.conflicting) return 'broken';
  return st.bodies.get(net.bodyOf(id)) ?? null;
}

/**
 * Why a new constraint `c` can't be made: the existing constraints (Fix included)
 * without which it could, and whether it could if the first pick were free to
 * move. Each trial is a dry solve of the network without one of them, so only
 * the first dozen are tried.
 */
function constraintCulprits(c: ObjectConstraint, held: string[], base = objConstraints()): { culprits: ObjectConstraint[]; heldBlocks: boolean } {
  const seeds = refsOf(c).map((r) => r.obj);
  const all = [...base, c];
  const net = constraintNetworkInput(seeds, held, all);
  const candidates = (net?.input.constraints ?? []).filter((x) => x !== c);
  for (const x of all) if (x.type === 'fix' && net?.input.bodies.has(net.bodyOf(x.a.obj)) && !candidates.includes(x)) candidates.push(x);
  const culprits = candidates.slice(0, 12).filter((x) => solveConstraints(seeds, held, { cs: all.filter((y) => y !== x), dry: true }));
  const heldBlocks = !culprits.length && held.length > 0 && solveConstraints(seeds, [], { cs: all, dry: true });
  return { culprits, heldBlocks };
}

/** Say why constraint `c` (added to `base`) was refused, naming what it clashes with. */
function explainConstraintRefusal(c: ObjectConstraint, held: string[], base = objConstraints()): void {
  const rel = CONSTRAINT_LABEL(c.type).toLowerCase();
  const { culprits, heldBlocks } = constraintCulprits(c, held, base);
  constraintUi.constraintClashIds = new Set(culprits.map((x) => x.id));
  if (culprits.length) {
    const names = culprits.slice(0, 3).map((x) => `${CONSTRAINT_LABEL(x.type)} (${refsOf(x).map((r) => constraintObjName(r.obj)).join(' – ')})`).join(', ');
    flashToolStatus(tr('Can’t make that {rel}: it clashes with {list}.').replace('{rel}', rel).replace('{list}', names));
  } else if (heldBlocks) {
    flashToolStatus(tr('Can’t make that {rel} while {obj} stays put — pick it second to move it instead.').replace('{rel}', rel).replace('{obj}', constraintObjName(held[0])));
  } else {
    flashToolStatus(tr('Can’t make that {rel}: it would conflict with the existing constraints, or the objects can’t move that way.').replace('{rel}', rel));
  }
  invalidate();
}

const CONSTRAINT_LABEL = (t: ObjConstraintType): string => tr(CONSTRAINT_META[t].label);

/** An object's name as the panels and the bar show it. */
export function constraintObjName(id: string): string {
  const n = connectionEndpointName(id);
  if (n !== '?') return n;
  if ((state.drawing.arcs || []).some((a) => a.id === id)) return tr('Arc');
  if ((state.drawing.leaders || []).some((l) => l.id === id)) return tr('Leader');
  if ((state.drawing.revisionClouds || []).some((c) => c.id === id)) return tr('Revision cloud');
  const sec = (state.drawing.sections || []).findIndex((x) => x.id === id);
  if (sec >= 0) return `${tr('Section')} ${sectionLabel(sec)}`;
  return '?';
}
/** A piece as the panels name it: `Sauna 1 · outer face · wall 2`. */
function constraintPieceName(ref: ConstraintRef): string {
  const parts = [constraintObjName(ref.obj)];
  if (ref.face === 'outer') parts.push(tr('outer face'));
  else if (ref.face === 'inner') parts.push(tr('inner face'));
  else if (ref.face === 'left') parts.push(tr('left face'));
  else if (ref.face === 'right') parts.push(tr('right face'));
  if (ref.el === 'vertex') parts.push(tr('vertex {n}').replace('{n}', String(ref.index + 1)));
  else if (ref.el === 'edge') parts.push(tr('edge {n}').replace('{n}', String(ref.index + 1)));
  else if (ref.el === 'curve') parts.push(tr('curve'));
  return parts.join(' · ');
}

// ---- The Constrain tool ----
// Hover highlights the piece under the cursor; click the first piece, then the
// second; a menu offers what is valid for the pair (or the relation typed first
// as a command-bar option is made straight away). The first pick HOLDS — the
// second object moves to satisfy the relation. The tool stays armed.
export let constrainFirst: CPiece | null = null;
/** Symmetric: the two vertices picked, waiting for the line they mirror about. */
export let constrainSym: { first: CPiece; second: CPiece } | null = null;

/** Every piece under the cursor, best first: the vertices within 10 px, nearest
 *  first, then the edges within 6 px. Only the objects in `constrainScope` when
 *  the tool was armed for a selection (Add relation…). */
function constraintPiecesAt(world: Vec2): CPiece[] {
  const vs: Array<[number, CPiece]> = [], es: Array<[number, CPiece]> = [];
  const tv = 10 / vp.scale, te = 6 / vp.scale;
  for (const o of groupables(state.drawing)) {
    if (constraintUi.constrainScope && !constraintUi.constrainScope.has(o.id)) continue;
    const g = constraintGeom(o.id, false);
    if (!g) continue;
    for (const p of g.pieces) {
      if (p.ref.el === 'vertex') { const d = distance(world, p.pts[0]); if (d < tv) vs.push([d, p]); }
      else if (p.outline) {
        let d = Infinity;
        for (let k = 0; k + 1 < p.outline.length; k++) d = Math.min(d, distToSegment(world, p.outline[k], p.outline[k + 1]));
        if (d < te) es.push([d, p]);
      } else { const d = distToSegment(world, p.pts[0], p.pts[1]); if (d < te) es.push([d, p]); }
    }
  }
  return [...vs.sort((a, b) => a[0] - b[0]), ...es.sort((a, b) => a[0] - b[0])].map(([, p]) => p);
}

/** The piece a click at `sp` takes: the best under it, or the one Tab chose. */
export function constraintPieceAt(world: Vec2, sp?: Vec2): CPiece | null {
  const all = constraintPiecesAt(world);
  if (!all.length) return null;
  const k = constraintUi.constrainCycle && sp && Math.hypot(sp.x - constraintUi.constrainCycle.sp.x, sp.y - constraintUi.constrainCycle.sp.y) <= 4 ? constraintUi.constrainCycle.idx % all.length : 0;
  return all[k];
}

/** Tab with the Constrain tool: offer the next piece stacked under the pointer. */
export function cycleConstraintPiece(sp: Vec2, dir: 1 | -1): void {
  const all = constraintPiecesAt(vp.screenToWorld(sp));
  if (all.length < 2) return;
  const was = constraintUi.constrainCycle && Math.hypot(sp.x - constraintUi.constrainCycle.sp.x, sp.y - constraintUi.constrainCycle.sp.y) <= 4 ? constraintUi.constrainCycle.idx : 0;
  const idx = (was + dir + all.length) % all.length;
  constraintUi.constrainCycle = { sp, idx };
  constraintUi.constrainHover = all[idx];
  setConstraintSnapMarker(constraintUi.constrainHover);
  flashToolStatus(`${tr('Overlapping pieces')}: ${idx + 1} / ${all.length} — ${constraintPieceName(all[idx].ref)}`);
  invalidate();
}

/** The AutoSnap marker on a hovered vertex piece — the same glyph and tooltip a
 *  snap shows (a part's centre, a circle's quadrant, a node…); off for an edge. */
export function setConstraintSnapMarker(p: CPiece | null): void {
  if (!p || p.ref.el !== 'vertex') { state.snapIndicator = null; state.snapIndicatorKind = null; return; }
  const id = p.ref.obj, i = p.ref.index;
  const inst = state.drawing.instances.find((x) => x.id === id);
  const poly = (state.drawing.polygons || []).find((x) => x.id === id);
  const arc = (state.drawing.arcs || []).find((x) => x.id === id);
  const kind: SnapKind = inst?.nodes && inst.nodes.length >= 2 ? 'node'
    : inst && !isPolyRoom(inst) && i === 4 ? 'center'
    : poly?.ellipse ? (i === 0 ? 'center' : 'quadrant')
    : arc && i === 2 ? 'center' : 'endpoint';
  state.snapIndicator = { ...p.pts[0] };
  state.snapIndicatorKind = kind;
}

/** The constraint options a typed letter can choose: what the first pick allows
 *  (or, before it, every relation), and Fix. */
export function constrainOptions(): ToolOption[] {
  const types = constrainFirst
    ? [...new Set([...relationsFor(constrainFirst.ref.el, 'vertex'), ...relationsFor(constrainFirst.ref.el, 'edge')])]
    : (Object.keys(CONSTRAINT_META) as ObjConstraintType[]).filter((t) => t !== 'fix');
  return [...types, 'fix' as const].map((t) => ({ id: t, label: CONSTRAINT_META[t].label }));
}

export function resetConstrainTool(): void {
  constrainFirst = null;
  constraintUi.constrainType = null;
  constraintUi.constrainHover = null;
  constraintUi.constrainCycle = null;
  constrainSym = null;
}

/** A click with the Constrain tool. */
export function constrainClick(e: MouseEvent, world: Vec2): void {
  const piece = constraintPieceAt(world, screenPos(e));
  if (!piece) { flashToolStatus(tr('Click a vertex or an edge of an object.')); return; }
  if (constraintUi.constrainType === 'fix') { toggleFix(piece.ref.obj); constraintUi.constrainType = null; updateStatus(); invalidate(); return; }
  if (constrainSym) {
    if (piece.ref.el !== 'edge') { flashToolStatus(tr('Pick a line — an edge — for the two vertices to mirror about.')); return; }
    const { first, second } = constrainSym;
    constrainSym = null;
    void createObjConstraint('symmetric', first, second, piece);
    return;
  }
  if (!constrainFirst) { constrainFirst = piece; updateStatus(); invalidate(); return; }
  const first = constrainFirst;
  if (sameRef(first.ref, piece.ref)) return; // the same piece again: keep waiting
  const types = relationsFor(first.ref.el, piece.ref.el);
  if (constraintUi.constrainType) {
    if (types.includes(constraintUi.constrainType)) { void createObjConstraint(constraintUi.constrainType, first, piece); return; }
    flashToolStatus(tr('{rel} needs other pieces — pick again.').replace('{rel}', CONSTRAINT_LABEL(constraintUi.constrainType)));
    constrainFirst = null; updateStatus(); invalidate();
    return;
  }
  // Opened on the next tick: this is a mousedown, and the window's own mousedown
  // listener (which closes any menu clicked outside) runs after it.
  const items = [types.map((t) => ({ label: CONSTRAINT_META[t].label, run: () => { void createObjConstraint(t, first, piece); } }))];
  setTimeout(() => openContextMenu(e, items), 0);
}

/**
 * Make constraint `type` between two picked pieces: the first holds, the second
 * object moves. A valued one asks for its value (the current measure). Refused,
 * with the reason in the bar, when it cannot be satisfied.
 */
async function createObjConstraint(type: ObjConstraintType, first: CPiece, second: CPiece, mirror?: CPiece): Promise<void> {
  // Symmetric takes a third pick: the line to mirror about.
  if (type === 'symmetric' && !mirror) { constrainSym = { first, second }; constrainFirst = first; updateStatus(); invalidate(); return; }
  const [a, b] = orderRefs(first.ref, second.ref);
  const pa = a === first.ref ? first : second, pb = a === first.ref ? second : first;
  let value: number | undefined;
  const meta = CONSTRAINT_META[type];
  if (meta.valued) {
    const cur = measure(type, pa, pb);
    const shown = meta.valued === 'deg' ? String(Math.round(cur * 100) / 100) : String(Math.round(cur));
    const raw = await showPrompt(CONSTRAINT_LABEL(type), meta.valued === 'deg' ? 'Angle (°)' : 'Distance (mm)', shown, 'OK');
    if (raw == null) return;
    const v = meta.valued === 'deg' ? evalFormula(raw) : parseMeasure(raw, 'mm');
    if (v == null || !Number.isFinite(v) || v < 0) { flashToolStatus(tr('That is not a valid value.')); return; }
    value = v;
  }
  // Two pieces of ONE polygon or room: that shape's own relation (one tool for both).
  if (a.obj === b.obj && !mirror) { sameObjectRelation(type, a, b, value); return; }
  const c: ObjectConstraint = { id: newId('con'), type, a, b, ...(mirror ? { m: mirror.ref } : {}), ...(value != null ? { value } : {}) };
  addObjConstraint(c, mirror ? [first.ref.obj, mirror.ref.obj] : [first.ref.obj]);
}

/** Add a constraint and satisfy it, holding `held`. One undo step. */
function addObjConstraint(c: ObjectConstraint, held: string[]): boolean {
  const cs = [...objConstraints(), c];
  const dup = objConstraints().some((x) => x.type === c.type && ((sameRef(x.a, c.a) && x.b && c.b && sameRef(x.b, c.b)) || (x.b && c.b && sameRef(x.a, c.b) && sameRef(x.b, c.a))));
  if (dup) { flashToolStatus(tr('Those pieces are already {rel}.').replace('{rel}', CONSTRAINT_LABEL(c.type).toLowerCase())); resetConstrainTool(); updateStatus(); invalidate(); return false; }
  const start = undoSteps.mark();
  const ok = solveConstraints(refsOf(c).map((r) => r.obj), held, { cs });
  if (!ok) { // nothing was moved: a failed solve applies nothing
    explainConstraintRefusal(c, held);
    resetConstrainTool(); updateStatus(); invalidate();
    return false;
  }
  // The solve has moved things already: the undo step starts from before it.
  undoSteps.push(start);
  (state.drawing.constraints ??= []).push(c);
  constraintSolved();
  syncDimensions();
  flashToolStatus(tr('{rel} added.').replace('{rel}', CONSTRAINT_LABEL(c.type)));
  resetConstrainTool();
  updateStatus(); renderProperties(); invalidate();
  return true;
}

/** Fix an object in place (or free it again). One undo step. */
export function toggleFix(id: string): void {
  pushHistory();
  const cs = objConstraints();
  const i = cs.findIndex((c) => c.type === 'fix' && c.a.obj === id);
  if (i >= 0) { cs.splice(i, 1); flashToolStatus(tr('{obj} is free to move again.').replace('{obj}', constraintObjName(id))); }
  else {
    (state.drawing.constraints ??= []).push({ id: newId('con'), type: 'fix', a: { obj: id, el: 'object', index: -1 } });
    flashToolStatus(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(id)));
  }
  constraintSolved();
  renderProperties(); invalidate();
}

/** Two pieces of the SAME object. A polygon or a room takes it as its own shape
 *  relation where one exists; a wall or line keeps it as an object constraint
 *  (it reshapes to satisfy it); a part, being rigid, refuses. */
function sameObjectRelation(type: ObjConstraintType, a: ConstraintRef, b: ConstraintRef, value?: number): void {
  const poly = (state.drawing.polygons || []).find((p) => p.id === a.obj && !p.ellipse);
  const room = state.drawing.instances.find((i) => i.id === a.obj && isPolyRoom(i));
  const done = (): void => { resetConstrainTool(); updateStatus(); renderProperties(); invalidate(); };
  if (poly || room) {
    // Room pieces are numbered by WALL; its relations by segment — a wall's first.
    const seg = (r: ConstraintRef): number => (room ? wallRuns(room)[r.index]?.segs[0] ?? r.index : r.index);
    const vtx = (r: ConstraintRef): number => (room ? wallRuns(room)[r.index]?.a ?? r.index : r.index);
    const ring = room ? room.polygon!.length : poly!.points.length;
    if ((a.el === 'edge' && seg(a) >= ring) || (b.el === 'edge' && seg(b) >= ring)) { flashToolStatus(tr('A hole can’t take a shape relation.')); done(); return; }
    let rel: ShapeRelation | null = null;
    if (a.el === 'edge' && b.el === 'edge' && (type === 'parallel' || type === 'perpendicular' || type === 'equal' || type === 'collinear')) rel = { type, edges: [seg(a), seg(b)] };
    else if (a.el === 'vertex' && b.el === 'vertex' && type === 'distance' && value != null) rel = { type: 'distancePP', verts: [vtx(a), vtx(b)], value: Math.round(value) };
    if (!rel) { flashToolStatus(tr('{rel} isn’t available inside one shape — use it between two objects.').replace('{rel}', CONSTRAINT_LABEL(type))); done(); return; }
    const ok = poly ? addPolyRelation(poly, rel) : addRoomRelation(room!, rel);
    if (!ok) flashToolStatus(tr('Can’t make that {rel}: it would conflict with the existing constraints, or the objects can’t move that way.').replace('{rel}', CONSTRAINT_LABEL(type).toLowerCase()));
    else flashToolStatus(tr('{rel} added.').replace('{rel}', CONSTRAINT_LABEL(type)));
    done();
    return;
  }
  const g = constraintGeom(a.obj);
  if (!g?.deform) { flashToolStatus(tr('A part is rigid: constrain it to another object.')); done(); return; }
  // The shape itself has to give: what it is constrained to holds instead.
  const others = [...constrainedObjectIds()].filter((id) => id !== a.obj);
  addObjConstraint({ id: newId('con'), type, a, b, ...(value != null ? { value } : {}) }, others);
}

/** Geometry fingerprints of every constrained object at the last solve — what
 *  `reconcileConstraints` compares against to see what an edit changed. */
let constraintPrints = new Map<string, string>();
/** Snapshots of each FIXED object at the last solve, to put one back if an edit
 *  moved it. */
let fixedSnapshots = new Map<string, string>();
function constraintPrint(id: string): string {
  const g = constraintGeom(id, false);
  if (!g) return '';
  return g.pieces.filter((p) => p.ref.el === 'vertex').map((p) => `${Math.round(p.pts[0].x * 100)},${Math.round(p.pts[0].y * 100)}`).join(';');
}
/** Where each constraint end's piece was at the last solve (`<id>|a`, `<id>|b`) —
 *  how `refindConstraintPieces` finds it again after an edit renumbered it. */
let constraintRefAt = new Map<string, Vec2[]>();
/** Every object id at the last solve: what is NEW since then (an explode's pieces,
 *  a trim's halves) is where a constraint whose object went may find its piece. */
let knownObjectIds = new Set<string>();
/** Record the drawing as solved: the next reconcile compares against this. */
export function constraintSolved(): void {
  constraintPrints = new Map([...constrainedObjectIds()].map((id) => [id, constraintPrint(id)]));
  knownObjectIds = new Set(constrainables().map((o) => o.id));
  constraintRefAt = new Map();
  for (const c of objConstraints()) {
    for (const [end, ref] of [['a', c.a], ['b', c.b], ['m', c.m]] as const) {
      const p = ref && ref.el !== 'object' ? constraintPiece(ref) : null;
      if (p) constraintRefAt.set(`${c.id}|${end}`, p.pts.map((q) => ({ ...q })));
    }
  }
  fixedSnapshots = new Map();
  for (const id of fixedObjectIds()) { const o = constrainables().find((x) => x.id === id); if (o) fixedSnapshots.set(id, JSON.stringify(o)); }
}

/** `ids` and every object constrained to them, directly or through others (the
 *  whole network they would carry along). */
export function withConstrainedPartners(ids: ReadonlySet<string> | string | null): ReadonlySet<string> | string | null {
  const cs = objConstraints().filter((c) => c.b);
  if (!cs.length || ids == null) return ids;
  const out = new Set(typeof ids === 'string' ? [ids] : ids);
  for (let grew = true; grew;) {
    grew = false;
    for (const c of cs) {
      const objs = refsOf(c).map((r) => r.obj);
      if (objs.some((o) => out.has(o)) && objs.some((o) => !out.has(o))) { for (const o of objs) out.add(o); grew = true; }
    }
  }
  for (const id of fixedObjectIds()) if (!(typeof ids === 'string' ? ids === id : ids.has(id))) out.delete(id);
  return out;
}

/** When the bar last said a fixed object was put back / a network failed, so a
 *  drag does not repeat it every frame. */
let constraintFlashAt = 0;
export function constraintFlash(msg: string): void {
  const now = performance.now();
  if (now - constraintFlashAt < 1500) return;
  constraintFlashAt = now;
  flashStatus(msg);
}

/** Put an object back as it was (a fixed object some edit moved). */
function restoreObjectJson(id: string, json: string): void {
  const lists: Array<Array<{ id: string }> | undefined> = [state.drawing.instances, state.drawing.polygons, state.drawing.walls,
    state.drawing.texts, state.drawing.arcs, state.drawing.leaders, state.drawing.revisionClouds, state.drawing.sections];
  for (const arr of lists) {
    const i = arr?.findIndex((x) => x.id === id) ?? -1;
    if (arr && i >= 0) { arr[i] = JSON.parse(json); dropLayerCache(); return; }
  }
}

/**
 * Re-point every constraint end whose object changed how many pieces it has, or
 * went (an explode, a join, a trim), at the piece now where its piece was at the
 * last solve (`matchPiece`). Only that object — or, for one that went, the
 * objects new or reshaped since — is searched, so a partner sitting on the same
 * spot is never taken for it. An end that can't be found takes its constraint
 * with it. Returns the list unchanged (the same array) when nothing moved.
 */
function refindConstraintPieces(cs: ObjectConstraint[]): { cs: ObjectConstraint[]; lost: number } {
  const objs = constrainables();
  const exists = new Set(objs.map((o) => o.id));
  const count = (pr: string | undefined): number => (pr ? pr.split(';').length : 0);
  const reshaped = new Set<string>();
  for (const [id, pr] of constraintPrints) if (exists.has(id) && count(pr) !== count(constraintPrint(id))) reshaped.add(id);
  const fresh = objs.filter((o) => !knownObjectIds.has(o.id) || reshaped.has(o.id)).map((o) => o.id);
  let touched = false, lost = 0;
  const out: ObjectConstraint[] = [];
  for (const c of cs) {
    let ok = true;
    const ends = [['a', c.a], ['b', c.b], ['m', c.m]] as const;
    const next: Partial<Record<'a' | 'b' | 'm', ConstraintRef>> = {};
    for (const [end, ref] of ends) {
      if (!ref) continue;
      const gone = !exists.has(ref.obj);
      if (!gone && !reshaped.has(ref.obj)) continue;
      const at = constraintRefAt.get(`${c.id}|${end}`);
      if (ref.el === 'object' || !at) { if (gone) ok = false; continue; }
      const pool = (gone ? fresh : [ref.obj]).flatMap((id) => (constraintGeom(id, false)?.pieces ?? [])
        .filter((p) => p.ref.el === ref.el && (gone || (p.ref.face ?? '') === (ref.face ?? ''))));
      // A curve is found again by its centre.
      const m = ref.el === 'curve' ? matchPiece([at[0]], pool.map((p) => ({ ref: p.ref, pts: [p.pts[0]] }))) : matchPiece(at, pool);
      if (!m) { ok = false; continue; }
      if (!sameRef(m.ref, ref)) { next[end] = { ...m.ref }; touched = true; }
    }
    if (!ok) { lost++; touched = true; continue; }
    out.push(next.a || next.b || next.m ? { ...c, ...(next.a ? { a: next.a } : {}), ...(next.b ? { b: next.b } : {}), ...(next.m ? { m: next.m } : {}) } : c);
  }
  return touched ? { cs: out, lost } : { cs, lost: 0 };
}

/**
 * Keep the drawing's constraints satisfied after ANY edit — run before every
 * model frame. Whatever changed since the last solve holds and its neighbours
 * follow; a FIXED object that moved is put back; a constraint whose object is
 * gone is dropped. A network that can't be satisfied is left as it is, its
 * badges red. No history of its own: the edit that changed things already
 * pushed it, so one undo takes the edit and what followed it back together.
 */
export function reconcileConstraints(): void {
  const all = state.drawing.constraints;
  if (!all?.length) { if (constraintPrints.size) { constraintPrints = new Map(); constraintRefAt = new Map(); } return; }
  const found = refindConstraintPieces(all);
  const kept = found.cs;
  if (kept !== all) {
    state.drawing.constraints = kept;
    if (state.selectedConstraintId && !kept.some((c) => c.id === state.selectedConstraintId)) state.selectedConstraintId = null;
    if (found.lost) constraintFlash(tr('{n} constraint(s) removed with what they held.').replace('{n}', String(found.lost)));
  }
  let rebase = kept !== all;
  const changed: string[] = [];
  for (const id of constrainedObjectIds(kept)) {
    const pr = constraintPrint(id);
    const was = constraintPrints.get(id);
    if (was == null) { constraintPrints.set(id, pr); rebase = true; continue; } // newly constrained / loaded: the baseline
    if (was === pr) {
      // Unmoved: refresh a fixed object's snapshot, so a put-back keeps its other edits.
      if (fixedSnapshots.has(id)) { const o = constrainables().find((x) => x.id === id); if (o) fixedSnapshots.set(id, JSON.stringify(o)); }
      continue;
    }
    const snap = fixedSnapshots.get(id);
    if (snap) {
      restoreObjectJson(id, snap);
      constraintFlash(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(id)));
      continue;
    }
    changed.push(id);
  }
  if (!changed.length) { if (rebase) constraintSolved(); return; }
  const live = state.dragMode !== 'none';
  // The edit holds and the neighbours follow; when they can't (a fixed object, a
  // rigid part that can't turn that way), the edit itself gives — it is drawn
  // toward where it was put, free to turn, and lands as close as its constraints
  // allow (a drag swings it about a corner tied to a fixed object) — and only past
  // that do they break.
  if (!solveConstraints(changed, changed, { live })) {
    if (solveConstraints(changed, [], { live, pull: changed })) constraintFlash(tr('Its constraints hold it there.'));
    else constraintFlash(tr('Some constraints can’t be satisfied — their badges are red.'));
  }
  constraintSolved();
}

// ---- One list of every constraint in the drawing ----
// Constraints live in four places: between objects (`Drawing.constraints`), a
// shape's own relations (`relations` / `polygonRelations`), its per-edge H / V
// locks and pinned lengths, and a room wall's pinned figures. `constraintEntries`
// is the ONE list of all of them — the Constraints tab and each
// object's Constraints card read it — so a constraint is found and removed the
// same way wherever it is stored.

interface ConstraintEntry {
  key: string;
  /** The object(s) it belongs to. */
  owners: string[];
  /** As the Parts tab lists it (`Horizontal: Polygon 01 · edge 2`), and as its
   *  owner's card does, without the owner (`Horizontal · edge 2`). */
  label: string;
  short: string;
  /** What it holds, world mm (each a vertex or a run), for the highlight / Zoom to. */
  pts: Vec2[][];
  broken: boolean;
  remove: () => void;
  /** An object constraint is selectable; the rest select their owner. */
  constraintId?: string;
}

/** The label a shape relation reads with, e.g. `Parallel · edges 1, 3`. */
function shapeRelationLabel(rel: ShapeRelation, where: (i: number) => string): string {
  const name: Record<ShapeRelation['type'], string> = {
    parallel: 'Parallel', perpendicular: 'Perpendicular', equal: 'Equal', collinear: 'Collinear',
    angleV: 'Angle', lockX: 'Lock X', lockY: 'Lock Y', distancePP: 'Distance',
  };
  const head = tr(name[rel.type]);
  if ('edges' in rel) return `${head} · ${where(rel.edges[0])}, ${where(rel.edges[1])}`;
  if (rel.type === 'distancePP') return `${head} ${formatDimension(rel.value, drawingDimStyle(state.drawing))} · ${tr('vertex {n}').replace('{n}', String(rel.verts[0] + 1))}, ${tr('vertex {n}').replace('{n}', String(rel.verts[1] + 1))}`;
  if (rel.type === 'angleV') return `${head} ${Math.round(rel.value * 10) / 10}° · ${tr('vertex {n}').replace('{n}', String(rel.vert + 1))}`;
  return `${head} · ${tr('vertex {n}').replace('{n}', String(rel.vert + 1))}`;
}

/** A shape's own constraints (a polygon's, or a room's outline), as entries. */
function shapeEntries(owner: string, name: string, ring: Vec2[], o: {
  relations?: ShapeRelation[]; locks?: Array<'h' | 'v' | null>; lengths?: Array<number | null>;
  edgeName: (i: number) => string;
  write: (k: { relations?: ShapeRelation[]; locks?: Array<'h' | 'v' | null>; lengths?: Array<number | null> }) => void;
}): ConstraintEntry[] {
  const n = ring.length;
  const edge = (i: number): Vec2[] => [ring[i % n], ring[(i + 1) % n]];
  const out: ConstraintEntry[] = [];
  const named = (short: string): { label: string; short: string } => ({ short, label: short.replace(' · ', `: ${name} · `) });
  (o.relations ?? []).forEach((rel, i) => {
    out.push({
      key: `${owner}|rel${i}`, owners: [owner], ...named(shapeRelationLabel(rel, o.edgeName)),
      pts: 'edges' in rel ? rel.edges.map(edge) : 'verts' in rel ? rel.verts.map((v) => [ring[v]]) : [[ring[rel.vert]]],
      broken: false,
      remove: () => { const next = (o.relations ?? []).filter((_, k) => k !== i); o.write({ relations: next.length ? next : undefined }); },
    });
  });
  (o.locks ?? []).forEach((l, i) => {
    if (!l) return;
    out.push({
      key: `${owner}|lock${i}`, owners: [owner], ...named(`${tr(l === 'h' ? 'Horizontal' : 'Vertical')} · ${o.edgeName(i)}`),
      pts: [edge(i)], broken: false,
      remove: () => { const next = [...(o.locks ?? [])]; next[i] = null; o.write({ locks: next.some((x) => x) ? next : undefined }); },
    });
  });
  (o.lengths ?? []).forEach((len, i) => {
    if (len == null) return;
    out.push({
      key: `${owner}|len${i}`, owners: [owner], ...named(`${tr('Length')} ${formatDimension(len, drawingDimStyle(state.drawing))} · ${o.edgeName(i)}`),
      pts: [edge(i)], broken: false,
      remove: () => { const next = [...(o.lengths ?? [])]; next[i] = null; o.write({ lengths: next.some((x) => x != null) ? next : undefined }); },
    });
  });
  return out;
}

/** Every constraint in the drawing, wherever it is stored. Removing one is one undo step. */
function constraintEntries(): ConstraintEntry[] {
  const out: ConstraintEntry[] = [];
  for (const c of objConstraints()) {
    out.push({
      key: c.id, owners: refsOf(c).map((r) => r.obj), constraintId: c.id,
      label: `${constraintBadgeText(c)}  ${CONSTRAINT_LABEL(c.type)}: ${constraintObjName(c.a.obj)}${c.b && c.b.obj !== c.a.obj ? ` — ${constraintObjName(c.b.obj)}` : ''}`,
      short: `${constraintBadgeText(c)}  ${CONSTRAINT_LABEL(c.type)}`,
      pts: refsOf(c).map((r) => (r.el === 'object' ? [] : constraintPieceOutline(r))).filter((p) => p.length),
      broken: constraintBroken(c), remove: () => deleteConstraint(c.id),
    });
  }
  const edgeName = (i: number): string => tr('edge {n}').replace('{n}', String(i + 1));
  for (const p of state.drawing.polygons || []) {
    if (p.ellipse) continue;
    out.push(...shapeEntries(p.id, constraintObjName(p.id), p.points, {
      relations: p.relations, locks: p.edgeConstraints, lengths: p.edgeLengths, edgeName,
      write: (k) => {
        pushHistory();
        if ('relations' in k) p.relations = k.relations;
        if ('locks' in k) p.edgeConstraints = k.locks;
        if ('lengths' in k) p.edgeLengths = k.lengths;
        renderProperties(); invalidate();
      },
    }));
  }
  for (const w of state.drawing.walls || []) {
    const lengths = wallSegPins(w);
    if (!lengths.some((v) => v != null)) continue;
    out.push(...shapeEntries(w.id, constraintObjName(w.id), w.points, {
      lengths, edgeName,
      write: (k) => {
        pushHistory();
        if ('lengths' in k) { if (k.lengths) w.segLengths = k.lengths; else delete w.segLengths; }
        renderProperties(); invalidate();
      },
    }));
  }
  for (const inst of state.drawing.instances) {
    if (!isPolyRoom(inst) || !inst.polygon) continue;
    const name = constraintObjName(inst.id);
    const ring = inst.polygon.map((q) => instanceToWorld(inst, q));
    out.push(...shapeEntries(inst.id, name, ring, {
      relations: inst.polygonRelations, locks: inst.polygonConstraints, lengths: inst.polygonEdgeLengths, edgeName,
      write: (k) => {
        pushHistory();
        if ('relations' in k) inst.polygonRelations = k.relations;
        if ('locks' in k) inst.polygonConstraints = k.locks;
        if ('lengths' in k) inst.polygonEdgeLengths = k.lengths;
        regenerateSauna(inst);
        renderProperties(); invalidate();
      },
    }));
    // A wall's pinned figures (a door's own width is held by default, so only an
    // explicit pin counts).
    wallRuns(inst).forEach((run, w) => {
      const a = ring[run.a], b = ring[run.b];
      const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const at = (s: number): Vec2 => ({ x: a.x + (b.x - a.x) * s / L, y: a.y + (b.y - a.y) * s / L });
      wallChain(inst, w).forEach((span, k) => {
        const pinned = span.kind === 'solid' ? span.locked : span.kind === 'door' ? !!(span.feature as SaunaDoor | undefined)?.lockedWidth && span.locked : span.locked;
        if (!pinned) return;
        out.push({
          key: `${inst.id}|pin${w}.${k}`, owners: [inst.id],
          ...(() => { const short = `${tr('Pinned figure')} ${formatDimension(span.t - span.s, drawingDimStyle(state.drawing))} · ${tr('wall {n}').replace('{n}', String(w + 1))}`; return { short, label: short.replace(' · ', `: ${name} · `) }; })(),
          pts: [[at(span.s), at(span.t)]], broken: false,
          remove: () => { pushHistory(); unlockWallSpan(span); regenerateSauna(inst); renderProperties(); invalidate(); },
        });
      });
    });
  }
  return out;
}

/** The entry under the pointer in a list (its pieces light up on the drawing). */
let hoverConstraintEntry: ConstraintEntry | null = null;

// ---- Badges, the selected constraint, its panel and the Parts-tab list ----
// Every constraint shows a badge beside each piece it holds: its glyph, and the
// figure of a valued one; red when the drawing no longer satisfies it. Which
// badges show: the selection's (on both ends, so its partners are marked too),
// or all of them while the Constrain tool is armed or Show constraints is on.
// A badge is clicked to select its constraint — its panel edits the figure and
// deletes it.

/** The badges as the last frame laid them out (screen px) — what clicks test. */
export let constraintBadgeBoxes: BadgeBox[] = [];

export function selectedConstraint(): ObjectConstraint | null {
  if (!state.selectedConstraintId || selectedCount(state, MULTI_KINDS) > 0) return null;
  return objConstraints().find((c) => c.id === state.selectedConstraintId) ?? null;
}

/** Does the drawing as it stands break the constraint? (A vanished piece does.) */
function constraintBroken(c: ObjectConstraint): boolean {
  if (c.type === 'fix') return !constraintGeom(c.a.obj, false);
  return constraintError(c, constraintPiece(c.a), c.b ? constraintPiece(c.b) : null, c.m ? constraintPiece(c.m) : null) > BROKEN_TOL;
}

function visibleConstraints(): ObjectConstraint[] {
  // A constraint on a hidden layer's object shows no badge (nothing to point at).
  const all = objConstraints().filter((c) => !refsOf(c).some((r) => objHidden(r.obj)));
  if (!all.length) return [];
  if (state.showConstraints || state.tool === 'constrain') return all;
  const sel = selectedObjectIds();
  const sc = selectedConstraint();
  return all.filter((c) => c === sc || c.id === hoverPartId || constraintUi.constraintClashIds.has(c.id) || refsOf(c).some((r) => sel.has(r.obj)));
}

/** The badge's text: the relation's glyph, and the figure of a valued one. */
function constraintBadgeText(c: ObjectConstraint): string {
  const meta = CONSTRAINT_META[c.type];
  if (!meta.valued || c.value == null) return meta.glyph;
  const v = meta.valued === 'deg' ? `${Math.round(c.value * 100) / 100}°` : formatDimension(c.value, drawingDimStyle(state.drawing));
  return `${meta.glyph} ${v}`;
}

/** Where a constraint's badge sits for one of its pieces: beside a vertex, off an
 *  edge's middle — away from the object's middle (into the room for an inner
 *  face) — or, for Fix, off the object's top-right. */
function constraintBadgeSpec(c: ObjectConstraint, ref: ConstraintRef, text: string): BadgeSpec | null {
  const g = constraintGeom(ref.obj, false);
  if (!g) return null;
  const verts = g.pieces.filter((p) => p.ref.el === 'vertex').map((p) => vp.worldToScreen(p.pts[0]));
  if (!verts.length) return null;
  const cen = { x: verts.reduce((s, v) => s + v.x, 0) / verts.length, y: verts.reduce((s, v) => s + v.y, 0) / verts.length };
  if (ref.el === 'object') {
    return { cid: c.id, anchor: { x: Math.max(...verts.map((v) => v.x)), y: Math.min(...verts.map((v) => v.y)) }, dir: { x: 1, y: -1 }, text };
  }
  const piece = g.pieces.find((p) => sameRef(p.ref, ref));
  if (!piece) return null;
  if (piece.outline) { // a curve: above its topmost point
    const top = piece.outline.map((p) => vp.worldToScreen(p)).reduce((m, q) => (q.y < m.y ? q : m));
    return { cid: c.id, anchor: top, dir: { x: 0, y: -1 }, text };
  }
  const s = piece.pts.map((p) => vp.worldToScreen(p));
  const flip = ref.face === 'inner' ? -1 : 1;
  if (s.length === 1) {
    const d = { x: s[0].x - cen.x, y: s[0].y - cen.y };
    const dir = Math.hypot(d.x, d.y) > 1e-6 ? { x: d.x * flip, y: d.y * flip } : { x: 1, y: -1 };
    return { cid: c.id, anchor: s[0], dir, text };
  }
  const mid = { x: (s[0].x + s[1].x) / 2, y: (s[0].y + s[1].y) / 2 };
  let n = { x: s[1].y - s[0].y, y: -(s[1].x - s[0].x) };
  if (n.x * (mid.x - cen.x) + n.y * (mid.y - cen.y) < 0) n = { x: -n.x, y: -n.y };
  return { cid: c.id, anchor: mid, dir: { x: n.x * flip, y: n.y * flip }, text };
}

/** The two points a distance constraint measures between (world mm): the two
 *  vertices; a vertex and its foot on the line; the first edge's middle and its
 *  foot on the second's line. Null for any other relation. */
function constraintDimPoints(c: ObjectConstraint): [Vec2, Vec2] | null {
  if (c.type !== 'distance' && c.type !== 'pointLineDistance' && c.type !== 'lineDistance') return null;
  const pa = constraintPiece(c.a), pb = c.b ? constraintPiece(c.b) : null;
  if (!pa || !pb) return null;
  if (c.type === 'distance') return [pa.pts[0], pb.pts[0]];
  const from = c.type === 'pointLineDistance' ? pa.pts[0] : { x: (pa.pts[0].x + pa.pts[1].x) / 2, y: (pa.pts[0].y + pa.pts[1].y) / 2 };
  const [q0, q1] = pb.pts;
  const d = { x: q1.x - q0.x, y: q1.y - q0.y }, L2 = d.x * d.x + d.y * d.y || 1;
  const t = ((from.x - q0.x) * d.x + (from.y - q0.y) * d.y) / L2;
  return [from, { x: q0.x + d.x * t, y: q0.y + d.y * t }];
}

/** Lay out this frame's badges (and remember them for the click test). A distance
 *  constraint is drawn as a DRIVING DIMENSION instead — its line between what it
 *  measures, and its figure, which is the badge a click selects and a double-click
 *  edits. */
export function constraintBadges(): Array<BadgeBox & { broken: boolean; on: boolean; line?: [Vec2, Vec2] }> {
  const cs = visibleConstraints();
  const specs: BadgeSpec[] = [];
  const broken = new Set<string>();
  const lines = new Map<number, [Vec2, Vec2]>();
  // The dimensions first: their figures take their places before the badges do.
  const dims = cs.map((c) => [c, constraintDimPoints(c)] as const).filter((x): x is readonly [ObjectConstraint, [Vec2, Vec2]] => !!x[1]);
  for (const [c, [w0, w1]] of dims) {
    if (constraintBroken(c)) broken.add(c.id);
    const s0 = vp.worldToScreen(w0), s1 = vp.worldToScreen(w1);
    const mid = { x: (s0.x + s1.x) / 2, y: (s0.y + s1.y) / 2 };
    let n = { x: s1.y - s0.y, y: -(s1.x - s0.x) };
    if (n.y > 0 || (n.y === 0 && n.x < 0)) n = { x: -n.x, y: -n.y }; // the figure sits above its line
    lines.set(specs.length, [s0, s1]);
    specs.push({ cid: c.id, anchor: mid, dir: Math.hypot(n.x, n.y) > 1e-6 ? n : { x: 0, y: -1 }, text: formatDimension(c.value ?? 0, drawingDimStyle(state.drawing)) });
  }
  for (const c of cs) {
    if (dims.some(([d]) => d === c)) continue;
    if (constraintBroken(c)) broken.add(c.id);
    const text = constraintBadgeText(c);
    for (const ref of refsOf(c)) { const sp = constraintBadgeSpec(c, ref, text); if (sp) specs.push(sp); }
  }
  constraintBadgeBoxes = layoutBadges(specs);
  const on = new Set([selectedConstraint()?.id, constraintUi.hoverConstraintId, hoverPartId, ...constraintUi.constraintClashIds].filter((x): x is string => !!x));
  return constraintBadgeBoxes.map((b, i) => ({ ...b, broken: broken.has(b.cid), on: on.has(b.cid), ...(lines.has(i) ? { line: lines.get(i)! } : {}) }));
}

/** Double-click on a valued constraint's figure / badge: edit its value in place
 *  (a length in the inline editor; an angle in its panel, where ° is typed). */
export function editConstraintValueAt(cid: string, sp: Vec2): void {
  const c = objConstraints().find((x) => x.id === cid);
  if (!c) return;
  pickConstraint(cid);
  const meta = CONSTRAINT_META[c.type];
  if (meta.valued === 'mm') {
    openFigureEditor({ kind: 'constraint', id: cid }, String(Math.round((c.value ?? 0) * 100) / 100), sp);
  } else if (meta.valued === 'deg') {
    const input = document.querySelector<HTMLInputElement>('#properties-body .prop-row input');
    input?.focus(); input?.select();
  }
}

/** The pieces the selected / hovered constraint holds, to light up. */
export function constraintHighlight(): Vec2[][] {
  const ids = new Set([selectedConstraint()?.id, constraintUi.hoverConstraintId, hoverPartId, ...constraintUi.constraintClashIds].filter((x): x is string => !!x));
  const out: Vec2[][] = [];
  // A shape's own constraint or a wall pin, hovered in a list.
  if (hoverConstraintEntry) out.push(...hoverConstraintEntry.pts);
  else if (hoverPartId?.includes('|')) { const e = constraintEntries().find((x) => x.key === hoverPartId); if (e) out.push(...e.pts); }
  if (!ids.size) return out;
  for (const c of objConstraints()) {
    if (!ids.has(c.id)) continue;
    for (const ref of refsOf(c)) {
      if (ref.el === 'object') {
        for (const p of constraintGeom(ref.obj, false)?.pieces ?? []) if (p.ref.el === 'edge') out.push(p.pts);
      } else {
        const p = constraintPieceOutline(ref);
        if (p.length) out.push(p);
      }
    }
  }
  return out;
}


/** Select a constraint (its badge or its row). Selection only — no history. */
export function pickConstraint(id: string): void {
  deselectAll();
  selectConstraint(state, id);
  renderProperties();
  invalidate();
}

/** Remove a constraint. One undo step; nothing moves. */
export function deleteConstraint(id: string): void {
  const c = objConstraints().find((x) => x.id === id);
  if (!c) return;
  pushHistory();
  state.drawing.constraints = objConstraints().filter((x) => x.id !== id);
  if (state.selectedConstraintId === id) state.selectedConstraintId = null;
  if (constraintUi.hoverConstraintId === id) constraintUi.hoverConstraintId = null;
  constraintSolved();
  flashStatus(tr('{rel} removed.').replace('{rel}', CONSTRAINT_LABEL(c.type)));
  renderProperties();
  invalidate();
}

/** Give a valued constraint a new figure and satisfy it (the first piece holds).
 *  Refused — nothing changes — when the network can't take it. */
export function setConstraintValue(c: ObjectConstraint, v: number): void {
  if (!Number.isFinite(v) || v < 0 || v === c.value) return;
  const start = undoSteps.mark();
  const old = c.value;
  c.value = v;
  const ok = solveConstraints(refsOf(c).map((r) => r.obj), [c.a.obj]);
  if (!ok) {
    c.value = old;
    explainConstraintRefusal({ ...c, value: v }, [c.a.obj], objConstraints().filter((x) => x !== c));
  } else {
    undoSteps.push(start);
    constraintSolved();
    syncDimensions();
  }
  renderProperties();
  invalidate();
}

/** The selected constraint's own panel. */
export function renderConstraintPanel(body: HTMLElement, c: ObjectConstraint): void {
  const meta = CONSTRAINT_META[c.type];
  body.appendChild(sectionTitle('Constraint'));
  body.appendChild(infoRow('Relation', `${meta.glyph}  ${CONSTRAINT_LABEL(c.type)}`));
  body.appendChild(infoRow(c.b ? 'First (holds)' : 'Object', constraintPieceName(c.a)));
  if (c.b) body.appendChild(infoRow('Second', constraintPieceName(c.b)));
  if (c.m) body.appendChild(infoRow('Mirror line', constraintPieceName(c.m)));
  if (meta.valued === 'mm') body.appendChild(numberRow('Distance (mm)', c.value ?? 0, (v) => setConstraintValue(c, v), { commit: true }));
  else if (meta.valued === 'deg') body.appendChild(numberRow('Angle (°)', c.value ?? 0, (v) => setConstraintValue(c, v), { commit: true }));
  body.appendChild(infoRow('Status', constraintBroken(c) ? tr('Not satisfied') : tr('Satisfied')));
  body.appendChild(deleteRow(() => deleteConstraint(c.id)));
}

/** A single object's Constraints card: Fix / Unfix, and each constraint it takes
 *  part in — a click selects it, ✕ removes it. */
export function appendObjectConstraintsCard(body: HTMLElement): void {
  const ids = [...selectedObjectIds()];
  if (ids.length !== 1 || !constraintGeom(ids[0])) return;
  const id = ids[0];
  hoverConstraintEntry = null; // its rows are being rebuilt
  const mine = objConstraints().filter((c) => c.type !== 'fix' && refsOf(c).some((r) => r.obj === id));
  body.appendChild(sectionTitle('Constraints'));
  const st = constraintStatusOf(id);
  if (st) {
    const row = infoRow('Status', st === 'defined' ? tr('Fully defined') : st === 'free' ? tr('Free to move') : tr('Not satisfied'));
    row.classList.add('constraint-status', st);
    body.appendChild(row);
  }
  body.appendChild(buttonRow(isFixedObject(id) ? 'Unfix' : 'Fix in place', () => toggleFix(id)));
  // The shape's own constraints and a wall's pinned figures, removable here too.
  for (const e of constraintEntries().filter((x) => !x.constraintId && x.owners.includes(id))) {
    const row = document.createElement('div');
    row.className = 'prop-row constraint-row';
    const name = document.createElement('span');
    name.className = 'constraint-name';
    name.textContent = e.short;
    const x = document.createElement('button');
    x.className = 'mini';
    x.textContent = '✕';
    x.title = tr('Remove this constraint');
    x.addEventListener('click', () => { hoverConstraintEntry = null; e.remove(); });
    row.append(name, x);
    row.addEventListener('mouseenter', () => { hoverConstraintEntry = e; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverConstraintEntry?.key === e.key) { hoverConstraintEntry = null; invalidate(); } });
    body.appendChild(row);
  }
  for (const c of mine) {
    const other = c.a.obj === id ? c.b : c.a;
    const row = document.createElement('div');
    row.className = 'prop-row constraint-row' + (constraintBroken(c) ? ' broken' : '');
    row.dataset.cid = c.id;
    const name = document.createElement('span');
    name.className = 'constraint-name';
    name.textContent = `${constraintBadgeText(c)}  ${CONSTRAINT_LABEL(c.type)}${other && other.obj !== id ? ` — ${constraintObjName(other.obj)}` : ''}`;
    name.addEventListener('click', () => pickConstraint(c.id));
    const x = document.createElement('button');
    x.className = 'mini';
    x.textContent = '✕';
    x.title = tr('Remove this constraint');
    x.addEventListener('click', () => deleteConstraint(c.id));
    row.append(name, x);
    row.addEventListener('mouseenter', () => { constraintUi.hoverConstraintId = c.id; invalidate(); });
    row.addEventListener('mouseleave', () => { if (constraintUi.hoverConstraintId === c.id) { constraintUi.hoverConstraintId = null; invalidate(); } });
    body.appendChild(row);
  }
}

/** The Constraints tab: every constraint in the drawing — between objects, a
 *  shape's own, a wall's pinned figures — one row each, red when broken. */
export function renderConstraintsTab(body: HTMLElement): void {
  // A selection shows its own constraints (any it is a piece of); nothing
  // selected, the whole drawing's.
  const sel = selectedObjectIds();
  const all = constraintEntries();
  const entries = sel.size ? all.filter((e) => e.owners.some((o) => sel.has(o))) : all;
  if (!entries.length) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = sel.size ? tr('The selection has no constraints.') : tr('No constraints yet. Add one with Modify ▸ Constrain.');
    body.appendChild(e);
    return;
  }
  body.appendChild(sectionTitle(sel.size ? 'Constraints of the selection' : 'Constraints'));
  for (const e of entries) {
    partObjectRow(body, {
      color: e.broken ? '#e5484d' : '#8fa3b8', label: e.label, // the badge's own colours: red only when broken
      active: !!e.constraintId && e.constraintId === selectedConstraint()?.id, hoverId: e.key,
      onClick: () => selectObjectFromList(() => { if (e.constraintId) selectConstraint(state, e.constraintId); else selectOwnerObject(e.owners[0]); }),
      menu: (ev) => showObjectRowMenu(ev, { zoomTo: () => zoomToPoints(e.pts.flat()), del: e.remove }),
    });
  }
}

/** Select a constraint's owner — a polygon or a room — on its own. */
function selectOwnerObject(id: string): void {
  if ((state.drawing.polygons || []).some((p) => p.id === id)) selectOne(state, 'polygon', id);
  else if (state.drawing.instances.some((i) => i.id === id)) selectOne(state, 'instance', id);
}
